import * as React from "react";
import {
  Link,
  Typography,
  Box,
  Paper,
  Grid2 as Grid,
  useMediaQuery,
} from "@mui/material";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import EditIcon from '@mui/icons-material/Edit';
import HotelIcon from '@mui/icons-material/Hotel';
import { useSnackbar } from 'notistack';
import { useAppStore } from "../../stores";

interface attendeeRecordProps {
  firstName: string;
  lastName: string;
  contactEmail: string;
  ccEmail: string;
  phone: string;
  address1: string;
  address2: string;
  retireeAttendee: boolean;
  city: string;
  stateCode: string;
  zip: string;
  emergencyContactPhone: string;
  attendeeId: number;
  reportedDate: any;
  isCertified: boolean;
  credentialTypeId?: string;
  confirmationNumber?: string;
}

interface ISuccessViewProps {
  savedAttendeeRecord: attendeeRecordProps;
  onEditClick: () => void;
}

const ConventionRegistrationFormSuccess: React.FC<ISuccessViewProps> = (props) => {
  const { enqueueSnackbar } = useSnackbar();
  const isMobile = useMediaQuery('(max-width:767px)');
  const isTabletPortrait = useMediaQuery('(min-width:768px) and (max-width:1023px)');
  const isTabletLandscape = useMediaQuery('(min-width:1024px) and (max-width:1439px)');
  const isTablet = isTabletPortrait || isTabletLandscape;

  const handleCopy = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    enqueueSnackbar(`${label} copied to clipboard!`, { variant: 'success' });
  };

  const activeConvention = useAppStore((state) => state.conventionReducer?.activeConvention);
  const cventPortalUrl: string = activeConvention?.conventionUrl as string || __CVENT_PORTAL_URL__;

  return (
    <Box sx={{ overflowX: 'hidden', width: '100%', maxWidth: '100%', px: (isMobile || isTablet) ? 2 : 0 }}>
      <Grid container spacing={(isMobile || isTablet) ? 0 : 4}>
        {/* Left Column - Success Message and Hotel Booking */}
        <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12 }} sx={{ px: (isMobile || isTablet) ? 0 : 0 }}>
          {/* Success Header */}
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <CheckCircleIcon sx={{ fontSize: 60, color: '#4caf50', mb: 2 }} />
            <Typography sx={{ 
              fontWeight: 600, 
              color: '#005DA5',
              fontSize: '32px',
              lineHeight: '125%',
              letterSpacing: '0.15px',
              fontFamily: 'Roboto',
              mb: 2 
            }}>
              Finalize your registration, below
            </Typography>
            <Link
              onClick={props.onEditClick}
              sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', gap: 0.5 }}
            >
              <EditIcon sx={{ fontSize: 22, color: '#1976D2' }} />
              Edit registration details
            </Link>
            <Typography sx={{ 
              color: '#666',
              fontWeight: 500,
              fontSize: '18px',
              lineHeight: '125%',
              letterSpacing: '0.15px',
              fontFamily: 'Roboto',
              mt: 2
            }}>
              Next step:
            </Typography>
          </Box>

          {/* Next Step Section */}
          <Paper elevation={2} sx={{ p: (isMobile || isTablet) ? 2 : 3, mb: 3 }}>
            <Link
              href={cventPortalUrl}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 3, textDecoration: 'none' }}
            >
              <HotelIcon sx={{
                fontSize: 24,
                color: '#005DA5',
                mt: '2px',
              }} />
              <Typography sx={{
                fontWeight: 600,
                fontFamily: 'Roboto',
                fontStyle: 'semi-bold',
                fontSize: '24px',
                lineHeight: '125%',
                letterSpacing: '0.15px',
                color: '#023A62'
              }}>
                Finish Your Registration
              </Typography>
            </Link>

            <Typography variant="body2" sx={{ 
              mb: 3, 
              color: '#444444',
              fontFamily: 'Roboto',
              fontWeight: 500,
              fontSize: '18px',
              lineHeight: '125%',
              letterSpacing: '0.15px'
            }}>
              You will need your email address and confirmation number to log in with Cvent and finalize your registration.
            </Typography>

            {/* Email with Copy */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1, flexWrap: isMobile ? 'wrap' : 'nowrap', gap: isMobile ? 1 : 0 }}>
              <Typography variant="body2" sx={{ fontFamily: 'Roboto', fontWeight: 700, fontSize: '16px', lineHeight: '125%', letterSpacing: '0.15px', wordBreak: 'break-word', overflowWrap: 'break-word', flex: 1, minWidth: 0 }}>
                {props.savedAttendeeRecord.contactEmail}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#1976D2', cursor: 'pointer', flexShrink: 0 }} onClick={() => handleCopy(props.savedAttendeeRecord.contactEmail, 'Email')}>
                <ContentCopyIcon fontSize="small" sx={{ color: '#1976D2' }} />
                <Typography variant="body2"> Copy</Typography>
              </Box>
            </Box>

            {/* Confirmation Number with Copy */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1, flexWrap: isMobile ? 'wrap' : 'nowrap', gap: isMobile ? 1 : 0 }}>
              <Typography variant="body2" sx={{ fontFamily: 'Roboto', fontWeight: 700, fontSize: '16px', lineHeight: '125%', letterSpacing: '0.15px', wordBreak: 'break-word', overflowWrap: 'break-word', flex: 1, minWidth: 0 }}>
                {props.savedAttendeeRecord.confirmationNumber}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#1976D2', cursor: 'pointer', flexShrink: 0 }} onClick={() => handleCopy(props.savedAttendeeRecord.confirmationNumber || '', 'Confirmation number')}>
                <ContentCopyIcon fontSize="small" sx={{ color: '#1976D2' }} />
                <Typography variant="body2"> Copy</Typography>
              </Box>
            </Box>

            {/* Reserve Hotel Link */}
            <Link
              href={cventPortalUrl}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                color: '#005DA5',
                textDecoration: 'none',
                fontSize: '24px',
                lineHeight: '150%',
                letterSpacing: '0.15px'
              }}
            >
              Click here to confirm your registration and reserve your hotel.
              <OpenInNewIcon sx={{ fontSize: 24, color: '#005DA5' }} />
            </Link>
          </Paper>

          {/* Reminder Message */}
          <Typography variant="body2" sx={{ 
            textAlign: 'center',               
            color: '#333333',
            fontFamily: 'Roboto',
            fontWeight: 600,
            fontSize: '20px',
            lineHeight: '125%',
            letterSpacing: '0.15px'
          }}>
            Continue to check your email for updates leading up to July!
          </Typography>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ConventionRegistrationFormSuccess;